/**
 * 案件モードの詳細（マスター・用語・画面）で、欄ごとに 変更前 ／ 変更後 を並べる 3 列の表。
 *
 * **中身を見る画面は左に as-is（公開中の仕様）、右に to-be（案件の姿）を並べる**（案件モードの
 * 原則）。1 行は欄 1 つ（画面の部品表なら部品 1 つ）で、同じ行の中に左右を置く。左右を別々の
 * 表にすると、折り返しの長さが左右で違ったときに段がずれ、隣り合うのが別の欄になる。
 *
 * 色は位置に名前を付けるだけである。変わった行は変更前を消えた側の赤、変更後を入った側の緑の
 * 地色にし、変わっていない行は両側を薄い字にする。片側に無い行（案件で足した・消した）は、
 * 無い側を斜線の空欄にして位置を保ち、「（無し）」は指すと出す。何も置かないと描画の失敗と区別が
 * 付かない。
 *
 * **差分が 1 つも無ければこの表を出さない。** 左右の両方に在り全行が変わっていない詳細は、
 * 案件なしと同じ通常の表示で描く（`isPjUnchanged`。Dhik の依頼 2026-09-29「diff がないものは
 * diff view を開けないようにしたい」）。同じ値を左右に並べても読む物は増えず、変わった行を
 * 探しに行って空振りするだけになる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードの詳細で、欄ごとに変更前と変更後を同じ行に並べ、変わった欄・足した欄・消した欄を色と斜線の空欄で示し、差分の無い詳細は比較の表にしないと判定する。
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { RowOpenButton, selectableRowProps } from './selectable-row.js';

/** 3 列の表の 1 行。 */
export interface PjCompareRow {
  /** 行の同一性（欄名・部品の番号）。 */
  key: string;
  /** 1 列目（欄名・部品名）。 */
  label: ReactNode;
  /** 変更前の中身。変更前に無ければ `null`。 */
  before: ReactNode | null;
  /** 変更後の中身。変更後に無ければ `null`。 */
  after: ReactNode | null;
  /** 左右で値が違うか。片側に無い行では使わない。 */
  changed: boolean;
}

/**
 * 1 行の状態。色と空札の置き方はこれで決まる。
 *
 * @param row 3 列の表の 1 行
 * @returns 追加・削除・変更・変更なしのどれか
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 3 列の表の 1 行を、片側の有無と値の違いから追加・削除・変更・変更なしへ分ける。
 * @graph-connects none
 */
export function pjCompareRowState(
  row: Pick<PjCompareRow, 'before' | 'after' | 'changed'>,
): 'created' | 'deleted' | 'updated' | 'unchanged' {
  if (row.before === null) return 'created';
  if (row.after === null) return 'deleted';
  return row.changed ? 'updated' : 'unchanged';
}

/** 表のセルの共通 class。 @graph-connects none */
const CELL_CLASS = 'border border-[var(--color-border)] px-3 py-2 text-left align-top text-sm';

/** 無い側の空札。斜線の地は線の色のトークンで描く。 @graph-connects none */
const ABSENT_CLASS =
  'bg-[repeating-linear-gradient(135deg,transparent_0_6px,var(--color-border)_6px_7px)] text-xs text-[var(--color-text-muted)]';

/** `PjCompareTable` の props。 */
export interface PjCompareTableProps {
  /** 1 列目の見出し（「欄」「部品」）。 */
  firstColumn: string;
  /** 上から並べる行。 */
  rows: readonly PjCompareRow[];
  /** 行を押せる表にするときの、押した行の鍵を受け取る動き。省略すれば行は押せない。 */
  onSelectRow?: (key: string) => void;
  /** 選んでいる行の鍵（印を付ける）。 */
  selectedKey?: string | null;
}

/**
 * 欄 ／ 変更前 ／ 変更後 の 3 列の表。
 *
 * 列見出しは「変更前」「変更後」だけにする。「公開中の仕様」は「変更前」を指すと出し、案件番号は
 * 共通のヘッダーの PJ モードの札が言うので書かない（Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個に
 * するぐらい情報を絞ってほしい」）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 欄ごとの変更前と変更後を 3 列の表で並べ、行の状態に応じて地色・薄い字・斜線の空欄を付ける。
 * @graph-connects pjCompareRowState [calls] 行の状態の判定
 */
export function PjCompareTable({
  firstColumn,
  rows,
  onSelectRow,
  selectedKey = null,
}: PjCompareTableProps) {
  const { t } = useTranslation();

  return (
    <table className="w-full table-fixed border-collapse">
      <thead>
        <tr>
          <th
            scope="col"
            className={`${CELL_CLASS} w-40 text-xs font-bold text-[var(--color-text-muted)]`}
          >
            {firstColumn}
          </th>
          <th
            scope="col"
            title={t('specDiff.paneBeforeNote')}
            className={`${CELL_CLASS} bg-[var(--color-diff-removed-pane)] text-xs font-bold text-[var(--color-bg-base)]`}
          >
            {t('specDiff.paneBefore')}
          </th>
          <th
            scope="col"
            className={`${CELL_CLASS} bg-[var(--color-diff-added-pane)] text-xs font-bold text-[var(--color-bg-base)]`}
          >
            {t('specDiff.paneAfter')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <PjCompareTableRow
            key={row.key}
            row={row}
            selected={row.key === selectedKey}
            onSelect={
              onSelectRow === undefined
                ? undefined
                : () => {
                    onSelectRow(row.key);
                  }
            }
          />
        ))}
      </tbody>
    </table>
  );
}

/**
 * 表の 1 行。状態ごとに左右のセルの地色と空札を決める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 3 列の表の 1 行を、追加・削除・変更・変更なしに応じた地色と空札で描き、押せる表なら選んだ行に印を付ける。
 * @graph-connects pjCompareRowState [calls] 行の状態の判定
 * @graph-connects selectableRowProps [calls] 行の押し方
 * @graph-connects RowOpenButton [calls] 行を開くボタン
 */
function PjCompareTableRow({
  row,
  selected,
  onSelect,
}: {
  row: PjCompareRow;
  selected: boolean;
  onSelect?: () => void;
}) {
  const { t } = useTranslation();
  const state = pjCompareRowState(row);
  const muted = state === 'unchanged' ? ' text-[var(--color-text-muted)]' : '';
  const beforeTone =
    state === 'updated' || state === 'deleted' ? ' bg-[var(--color-diff-removed-cell)]' : '';
  const afterTone =
    state === 'updated' || state === 'created' ? ' bg-[var(--color-diff-added-cell)]' : '';
  const strike = state === 'deleted' ? ' line-through' : '';

  return (
    <tr
      data-state={state}
      {...(onSelect === undefined ? {} : selectableRowProps(onSelect, selected))}
    >
      <th
        scope="row"
        className={`${CELL_CLASS} text-xs font-normal text-[var(--color-text-muted)]`}
      >
        {onSelect === undefined ? (
          row.label
        ) : (
          <RowOpenButton expanded={selected}>{row.label}</RowOpenButton>
        )}
      </th>
      {row.before === null ? (
        <td
          data-side="before"
          data-absent="true"
          title={t('pjCompare.absent')}
          className={`${CELL_CLASS} ${ABSENT_CLASS}`}
        />
      ) : (
        <td data-side="before" className={`${CELL_CLASS}${beforeTone}${muted}${strike}`}>
          {row.before}
        </td>
      )}
      {row.after === null ? (
        <td
          data-side="after"
          data-absent="true"
          title={t('pjCompare.absent')}
          className={`${CELL_CLASS} ${ABSENT_CLASS}`}
        />
      ) : (
        <td data-side="after" className={`${CELL_CLASS}${afterTone}${muted}`}>
          {row.after}
        </td>
      )}
    </tr>
  );
}

/** 左右の姿と、それぞれが在るか。 */
export interface PjSides<T> {
  /** 変更前の姿。変更前に無い（案件で足した）なら null。 */
  before: T | null;
  /** 変更後の姿。変更後に無い（案件で消した）なら null。 */
  after: T | null;
}

/**
 * 左右の姿から、欄ごとの行を組む。欄の値の比べ方は `compareKey` が決め、並びは `fields` の順。
 *
 * 見た目（描いた中身）で比べない。参照先の名前が同じでも指す先が変わった欄を、変わっていない
 * と読み違えるためである。
 *
 * @param sides 左右の姿
 * @param fields 欄の定義（鍵・見出し・比べる文字列・描く中身）
 * @returns 3 列の表の行
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 左右の姿と欄の定義から、欄ごとの変更前・変更後と変わったかどうかを組む。
 * @graph-connects none
 */
export function buildPjCompareRows<T>(
  sides: PjSides<T>,
  fields: readonly {
    key: string;
    label: ReactNode;
    compareKey: (value: T) => string;
    render: (value: T) => ReactNode;
  }[],
): PjCompareRow[] {
  return fields.map((field) => {
    const before = sides.before === null ? null : field.render(sides.before);
    const after = sides.after === null ? null : field.render(sides.after);
    const changed =
      sides.before !== null &&
      sides.after !== null &&
      field.compareKey(sides.before) !== field.compareKey(sides.after);
    return { key: field.key, label: field.label, before, after, changed };
  });
}

/**
 * 案件でその詳細が何も変わっていないか。左右の両方に在り、全行が変わっていないときだけ真。
 *
 * 真なら呼び出し側は 3 列の表を出さず、案件なしと同じ通常の表示で描く。片側に無い（案件で
 * 足した・消した）詳細は、比べる欄が 1 つも無くても差分が有る扱いにする — 行の状態だけで
 * 判定すると、欄を持たない新設のマスターが「変わっていない」に倒れる。
 *
 * @param sides 左右の姿
 * @param rows 3 列の表の行
 * @returns 差分が 1 つも無ければ true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードの詳細が、左右の両方に在り全行が変わっていないかを判定する。
 * @graph-connects pjCompareRowState [calls] 行の状態の判定
 */
export function isPjUnchanged<T>(
  sides: PjSides<T>,
  rows: readonly Pick<PjCompareRow, 'before' | 'after' | 'changed'>[],
): boolean {
  return (
    sides.before !== null &&
    sides.after !== null &&
    rows.every((row) => pjCompareRowState(row) === 'unchanged')
  );
}
